import { useState, useEffect, useMemo } from 'react'

import { useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faUser,
    faEnvelope,
    faLock,
    // faShieldAlt,
    faSignOutAlt,
    faArrowLeft,
    // faPen,
    faUserCircle
} from '@fortawesome/free-solid-svg-icons'

import { Button, Spinner } from '@/components/ui'
import { useAuthStore } from '@/stores/useAuthStore'
import { ROUTES } from '@/constants/routes'
import { sanitizeUserProfile } from '@/lib/sanitize'
import type { UserDetails } from '@/types/auth'
import styles from './Profile.module.css'

export function Profile() {
    const navigate = useNavigate()
    const { logOut, getUser } = useAuthStore()
    const [userDetails, setUserDetails] = useState<UserDetails | undefined>(() => getUser() ?? undefined)
    const [loggingOut, setLoggingOut] = useState(false)

    useEffect(() => {
        setUserDetails(getUser() ?? undefined)
    }, [getUser])

    // Sanitize user data before display
    const sanitizedProfile = useMemo(() => {
        if (!userDetails) return null;
        return sanitizeUserProfile(userDetails);
    }, [userDetails]);

    const handleLogout = async () => {
        setLoggingOut(true)
        await logOut()
        navigate('/')
    }

    const handleChangePassword = () => {
        navigate(ROUTES.changePassword)
    }

    // const handleSetup2FA = () => {
    //     toast.error("Próximamente")
    // }

    // const handleEditProfile = () => {
    //     toast.error('Próximamente')
    // }

    if (!userDetails || !sanitizedProfile) {
        return (
            <div className={styles.container}>
                <div className={styles.error}>
                    <p>No se pudieron cargar los datos del perfil</p>
                    <Button onClick={() => navigate(ROUTES.login)}>Ir a iniciar sesión</Button>
                </div>
            </div>
        )
    }

    const displayName = sanitizedProfile.username || sanitizedProfile.email?.split('@')[0] || 'Usuario'
    const initials = displayName.charAt(0).toUpperCase()

    return (
        <div className={styles.container}>
            <header className={styles.header}>
                <button
                    className={styles.backButton}
                    onClick={() => navigate(ROUTES.home)}
                >
                    <FontAwesomeIcon icon={faArrowLeft} />
                    <span>Volver al inicio</span>
                </button>
            </header>

            <div className={styles.mainCard}>
                {/* Profile Header */}
                <div className={styles.profileHeader}>
                    <div className={styles.avatar}>
                        <span>{initials}</span>
                    </div>
                    <div className={styles.profileInfo}>
                        <h1 className={styles.userName}>{displayName}</h1>
                        <p className={styles.userEmail}>{sanitizedProfile.email}</p>
                    </div>
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleLogout}
                        disabled={loggingOut}
                        className={styles.logoutButton}
                    >
                        {loggingOut ? (
                            <Spinner size="sm" variant="primary" />
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faSignOutAlt} />
                                Cerrar Sesión
                            </>
                        )}
                    </Button>
                </div>

                <div className={styles.divider} />

                {/* Personal Information */}
                <section className={styles.section}>
                    <div className={styles.sectionHeader}>
                        <h2 className={styles.sectionTitle}>Información Personal</h2>
                        {/* <Button variant="ghost" size="sm" onClick={handleEditProfile} className={styles.editButton}>
                            <FontAwesomeIcon icon={faPen} />
                            Editar
                        </Button> */}
                    </div>

                    <div className={styles.fieldGroup}>
                        <div className={styles.fieldRow}>
                            <div className={styles.field}>
                                <div className={styles.fieldIcon}>
                                    <FontAwesomeIcon icon={faUserCircle} />
                                </div>
                                <div className={styles.fieldContent}>
                                    <label className={styles.fieldLabel}>Usuario</label>
                                    <p className={styles.fieldValue}>{sanitizedProfile.username || '-'}</p>
                                </div>
                            </div>

                            <div className={styles.field}>
                                <div className={styles.fieldIcon}>
                                    <FontAwesomeIcon icon={faEnvelope} />
                                </div>
                                <div className={styles.fieldContent}>
                                    <label className={styles.fieldLabel}>Correo</label>
                                    <p className={styles.fieldValue}>{sanitizedProfile.email}</p>
                                </div>
                            </div>
                        </div>

                        <div className={styles.fieldRow}>
                            <div className={styles.field}>
                                <div className={styles.fieldIcon}>
                                    <FontAwesomeIcon icon={faUser} />
                                </div>
                                <div className={styles.fieldContent}>
                                    <label className={styles.fieldLabel}>Nombre</label>
                                    <p className={styles.fieldValue}>{sanitizedProfile.first_name || '-'}</p>
                                </div>
                            </div>

                            <div className={styles.field}>
                                <div className={styles.fieldIcon}>
                                    <FontAwesomeIcon icon={faUser} />
                                </div>
                                <div className={styles.fieldContent}>
                                    <label className={styles.fieldLabel}>Apellido</label>
                                    <p className={styles.fieldValue}>{sanitizedProfile.last_name || '-'}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <div className={styles.divider} />

                {/* Security Settings */}
                <section className={styles.section}>
                    <div className={styles.sectionHeader}>
                        <h2 className={styles.sectionTitle}>Seguridad</h2>
                    </div>

                    <div className={styles.settingsGroup}>
                        <div className={styles.settingItem}>
                            <div className={styles.settingIcon}>
                                <FontAwesomeIcon icon={faLock} />
                            </div>
                            <div className={styles.settingContent}>
                                <h3 className={styles.settingTitle}>Contraseña</h3>
                                <p className={styles.settingDescription}>Actualiza tu contraseña regularmente</p>
                            </div>
                            <Button variant="secondary" size="sm" onClick={handleChangePassword}>
                                Cambiar
                            </Button>
                        </div>

                        {/* <div className={styles.settingItem}>
                            <div className={styles.settingIcon}>
                                <FontAwesomeIcon icon={faShieldAlt} />
                            </div>
                            <div className={styles.settingContent}>
                                <h3 className={styles.settingTitle}>Autenticación en 2 pasos</h3>
                                <p className={styles.settingDescription}>Añade una capa extra de seguridad</p>
                            </div>
                            <Button variant="primary" size="sm" onClick={handleSetup2FA}>
                                Configurar
                            </Button>
                        </div> */}
                    </div>
                </section>
            </div>
        </div>
    )
}
